import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";

/**
 * Watches for a signed-in → signed-out transition after initial mount.
 * When detected: fires a toast and redirects to Clerk sign-in, preserving
 * the current URL so the user lands back on the right page.
 *
 * This redirect stays unconditional, unlike the service-worker update prompt
 * (lib/pwa.ts, #623): a session that has already expired cannot be deferred
 * with a "save first" choice the way a pending deploy can. The old rationale
 * here — "learn's attendance UI fires mutations on every dropdown change, so
 * there is no unsaved input to preserve" — no longer holds as a blanket claim
 * now that the cohort/session notes editors (components/cohort-notes.tsx,
 * session-notes.tsx) hold unsaved textarea input; it was never about this
 * redirect's own correctness, only a note that URL preservation was enough
 * for the v1 scenario. Losing an in-progress notes edit to an actual session
 * expiry is accepted as-is — only the SW-reload path (lib/pwa.ts) is gated.
 */
export function useSessionExpiration() {
  const { isLoaded, isSignedIn } = useAuth();

  // Track whether the user was previously signed in (after auth has loaded).
  const wasSignedIn = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isLoaded) return;

    if (wasSignedIn.current === null) {
      // First time auth resolves — record the initial state without reacting.
      wasSignedIn.current = isSignedIn ?? false;
      return;
    }

    // Transition: was signed in, now isn't.
    if (wasSignedIn.current && !isSignedIn) {
      const returnUrl = window.location.pathname + window.location.search;
      toast.error("Session expired — sign in again");
      // Redirect to Clerk hosted sign-in page, preserving current URL.
      const signInUrl = `/sign-in?redirect_url=${encodeURIComponent(returnUrl)}`;
      window.location.replace(signInUrl);
    }

    wasSignedIn.current = isSignedIn ?? false;
  }, [isLoaded, isSignedIn]);
}
